import React from 'react';

import {buttonPrimary, buttonSecondary} from '../lib/buttonStyles.js';

// ============================================================================
// Page states (Design Constitution N10, N12, F05, C20)
// ----------------------------------------------------------------------------
//   NotFoundCard  N10: an in-app card under the header — 'We can't find that
//                 page', the link in gray, a green 'Go Back'.
//   NoAccessCard  N10: a separate card — 'No Access — ask an admin'.
//   CrashCard     N12: 'Something Went Wrong', green Reload + secondary Go
//                 Home. PageErrorBoundary renders it per page, below the
//                 header, so the header stays.
//   BootSplash    F05/C20: the one branded splash until the header renders —
//                 brand-dark, the wordmark and a thin progress bar, no
//                 spinner; reduced motion keeps the bar still (index.html).
// The cards never decide access or routing: the caller that already owns
// that decision renders the card instead of redirecting.
// ============================================================================

const cardStyle: React.CSSProperties = {
  maxWidth: 520,
  margin: '48px auto',
  padding: '28px 28px 24px',
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 18,
  boxShadow: 'var(--shadow-card)',
  textAlign: 'center',
};
const titleStyle: React.CSSProperties = {margin: 0, fontSize: 22, fontWeight: 800, color: 'var(--ink)'};
const lineStyle: React.CSSProperties = {
  margin: '8px 0 0',
  fontSize: 14,
  color: 'var(--text-2)',
  overflowWrap: 'anywhere',
};
const actionsStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  gap: 8,
  marginTop: 20,
  flexWrap: 'wrap',
};

// 'Go Back' goes to the parent list (N10), which the caller resolves through
// the navigation owner (lib/parentRoute) — never history.back(), which could
// land on any unrelated page.
export function NotFoundCard({path, onBack}: {path?: string; onBack: () => void}) {
  return (
    <section data-page-state="not-found" role="status" style={cardStyle}>
      <h1 style={titleStyle}>We can&apos;t find that page</h1>
      {path && <p style={lineStyle}>{path}</p>}
      <div style={actionsStyle}>
        <button
          type="button"
          className="wcf-clickable wcf-btn wcf-btn--primary"
          data-page-state-back="1"
          onClick={onBack}
          style={buttonPrimary}
        >
          Go Back
        </button>
      </div>
    </section>
  );
}

export function NoAccessCard({onBack}: {onBack: () => void}) {
  return (
    <section data-page-state="no-access" role="status" style={cardStyle}>
      <h1 style={titleStyle}>No Access</h1>
      <p style={lineStyle}>Ask an admin if you need this page.</p>
      <div style={actionsStyle}>
        <button
          type="button"
          className="wcf-clickable wcf-btn wcf-btn--primary"
          data-page-state-back="1"
          onClick={onBack}
          style={buttonPrimary}
        >
          Go Back
        </button>
      </div>
    </section>
  );
}

export function CrashCard({onReload, onGoHome}: {onReload: () => void; onGoHome: () => void}) {
  return (
    <section data-page-state="crash" role="alert" style={cardStyle}>
      <h1 style={titleStyle}>Something Went Wrong</h1>
      <p style={lineStyle}>This page hit a problem. Reloading usually fixes it.</p>
      <div style={actionsStyle}>
        <button
          type="button"
          className="wcf-clickable wcf-btn wcf-btn--secondary"
          data-page-state-home="1"
          onClick={onGoHome}
          style={buttonSecondary}
        >
          Go Home
        </button>
        <button
          type="button"
          className="wcf-clickable wcf-btn wcf-btn--primary"
          data-page-state-reload="1"
          onClick={onReload}
          style={buttonPrimary}
        >
          Reload
        </button>
      </div>
    </section>
  );
}

type BoundaryProps = {
  children: React.ReactNode;
  onGoHome: () => void;
  resetKey?: unknown;
  // The page's header, drawn above the card so it stays when a page crashes.
  header?: React.ReactNode;
  onError?: (error: unknown, info: React.ErrorInfo) => void;
};
type BoundaryState = {failed: boolean; key: unknown};

// One page's crash stays inside that page: the header and the rest of the app
// keep running. A new resetKey (the route) clears the failure.
export class PageErrorBoundary extends React.Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = {failed: false, key: this.props.resetKey};

  static getDerivedStateFromProps(props: BoundaryProps, state: BoundaryState): BoundaryState | null {
    if (props.resetKey !== state.key) return {failed: false, key: props.resetKey};
    return null;
  }

  static getDerivedStateFromError(): Partial<BoundaryState> {
    return {failed: true};
  }

  componentDidCatch(error: unknown, info: React.ErrorInfo) {
    this.props.onError?.(error, info);
  }

  render() {
    if (this.state.failed) {
      return (
        <div data-page-crash="1" style={{minHeight: '100vh', background: 'var(--bg-page)'}}>
          {this.props.header}
          <CrashCard
            onReload={() => {
              if (typeof window !== 'undefined') window.location.reload();
            }}
            onGoHome={this.props.onGoHome}
          />
        </div>
      );
    }
    return this.props.children;
  }
}

export function BootSplash({label = 'Loading'}: {label?: string}) {
  return (
    <div data-boot-splash="1" role="status" aria-live="polite" className="wcf-boot-splash">
      <div className="wcf-boot-splash__mark">WCF Planner</div>
      <div className="wcf-boot-splash__bar" aria-hidden="true">
        <span />
      </div>
      <span className="wcf-visually-hidden">{label}</span>
    </div>
  );
}
